import { head, isEmpty } from 'lodash';
import React, { ChangeEvent, FC, FormEvent, useState } from 'react';
import { useUpdateEffect } from 'usehooks-ts';

import ArrowIcon from './assets/arrow-right.inline.svg';

interface Props {
  onSuccess?: (status: boolean) => void;
  buttonClassName?: string;
  containerClassName?: string;
}

export const SignupNewsletterForm: FC<Props> = ({
  onSuccess,
  buttonClassName = 'fill-white bg-accent-orange',
  containerClassName = 'border-2 border-ocean-blue',
}) => {
  const [email, setEmail] = useState<string>();
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);

  useUpdateEffect(() => {
    onSuccess && onSuccess(success);
  }, [success]);

  const handleEmailChange = (e: ChangeEvent) => {
    setError(undefined);
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    setEmail((e.target as HTMLInputElement).value);
  };
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setError(undefined);
    setLoading(true);

    const PORTAL_ID = 3455840;
    const FORM_ID = '260284f4-3748-4fc5-8fa0-a6b0427c908d';

    const hutk = ('; ' + document.cookie).split(`; hubspotutk=`).pop()?.split(';');

    const body = {
      submittedAt: +new Date(), // This millisecond timestamp is optional. Update the value from "1517927174000" to avoid an INVALID_TIMESTAMP error.
      fields: [
        {
          objectTypeId: '0-1',
          name: 'email',
          value: email,
        },
      ],
      context: {
        ...(!isEmpty(head(hutk)) ? { hutk: head(hutk) } : {}),
        pageName: document.title,
        pageUri: window.location.href,
        referrer: document.referrer,
      },
      legalConsentOptions: {
        consent: {
          // Include this object when GDPR options are enabled
          consentToProcess: true,
          text: 'I agree to allow CircleOf to store and process my personal data.',
          communications: [
            {
              value: true,
              subscriptionTypeId: 4072155,
              text: 'I agree to receive other communications from CircleOf.',
            },
          ],
        },
      },
    };

    // Grab public IP
    const ipResponse = await fetch('https://api.ipify.org', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (ipResponse.ok) {
      (body.context as any).ipAddress = await ipResponse.text();
    }

    try {
      // Sned to hubspot
      const response = await fetch(`https://api.hsforms.com/submissions/v3/integration/submit/${PORTAL_ID}/${FORM_ID}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error('An error has occurred!');
      }

      setEmail(undefined);
      setSuccess(true);
      setLoading(false);
    } catch (e: unknown) {
      setError((e as any).message);
      setSuccess(false);
      setLoading(false);
    }
  };

  return (
    <>
      {!!error && <span className="block mb-2 text-sm text-red-500 font-medium">{error}</span>}

      <form
        onSubmit={handleSubmit}
        style={error ? { borderColor: 'red' } : {}}
        className={`rounded-full overflow-hidden relative flex items-center flex-row ${containerClassName}`}
      >
        <input
          disabled={loading}
          onChange={handleEmailChange}
          name="email"
          style={{ minHeight: 46, color: 'rgba(22, 56, 78, .6)' }}
          type="email"
          placeholder="Enter your email here"
          className="mr-12 w-full pl-6 bg-transparent outline-none autofill:!bg-transparent"
        />
        <button
          disabled={loading}
          style={{ marginRight: 4 }}
          type="submit"
          className={`absolute right-0 rounded-full w-10 h-10 flex items-center justify-center ${buttonClassName}`}
        >
          <ArrowIcon />
        </button>
      </form>
    </>
  );
};

export default SignupNewsletterForm;
